/**
 * Overview tab — landing for the Researcher Knowledge portal.
 *
 * Shows top-line counts (corpus size, pending retag splits, framework
 * credibility coverage) plus a recent-activity panel sourced from
 * research_curation_log.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle, BookOpen, CheckCircle2, Clock, Database,
  FileText, FlaskConical, Microscope, ShieldCheck, Users,
} from 'lucide-react';
import { adminFetch } from '../../../utils/adminApi';
import type { PortalStats, AuditLogItem, PaginatedResponse } from './types';

interface KpiProps {
  label: string;
  value: number | string;
  sublabel?: string;
  icon: React.ComponentType<{ className?: string }>;
  tone?: 'neutral' | 'good' | 'warn';
}

function Kpi({ label, value, sublabel, icon: Icon, tone = 'neutral' }: KpiProps) {
  const toneColors = {
    neutral: 'text-gray-700 bg-gray-100',
    good: 'text-emerald-700 bg-emerald-50',
    warn: 'text-amber-700 bg-amber-50',
  };
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
          {label}
        </span>
        <span className={`flex h-8 w-8 items-center justify-center rounded-md ${toneColors[tone]}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-2 text-2xl font-semibold text-gray-900">{value}</div>
      {sublabel && <div className="mt-1 text-xs text-gray-500">{sublabel}</div>}
    </div>
  );
}

const ACTION_LABELS: Record<string, string> = {
  retag_approve: 'Retag approved',
  retag_reject: 'Retag rejected',
  retag_edit: 'Retag edited',
  retag_batch_approve: 'Retags batch-approved',
  candidate_publish: 'Candidate published',
  candidate_reject: 'Candidate rejected',
  candidate_edit: 'Candidate edited',
  source_demote: 'Source demoted',
  source_delete: 'Source deleted',
  framework_publish: 'Framework published',
  framework_reject: 'Framework rejected',
  framework_edit: 'Framework edited',
};

export function OverviewTab() {
  const stats = useQuery({
    queryKey: ['rk', 'stats'],
    queryFn: () => adminFetch<PortalStats>('/api/admin/researcher-knowledge/stats'),
    refetchInterval: 60_000,
  });

  const recentLog = useQuery({
    queryKey: ['rk', 'audit-log', 'recent'],
    queryFn: () =>
      adminFetch<PaginatedResponse<AuditLogItem>>(
        '/api/admin/researcher-knowledge/audit-log?since_days=14&page_size=15'
      ),
  });

  if (stats.isLoading) {
    return <div className="p-8 text-center text-gray-500">Loading stats…</div>;
  }
  if (stats.isError) {
    return (
      <div className="p-8 text-center text-red-600">
        Failed to load stats. Check that the researcher-knowledge router is deployed and migrations 1232+1233 are applied.
      </div>
    );
  }

  const s = stats.data!;

  return (
    <div className="space-y-6">
      {/* Section: Corpus size */}
      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Corpus
        </h3>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Kpi label="Academic Articles" value={s.sources.academic_total.toLocaleString()}
               icon={Microscope} sublabel={`${s.sources.academic_via_nkcat} via NK-CAT`} />
          <Kpi label="Practitioner Knowledge" value={s.sources.practitioner_total.toLocaleString()}
               icon={BookOpen} />
          <Kpi label="Frameworks" value={s.frameworks.total}
               icon={FlaskConical}
               sublabel={`${s.frameworks.with_credibility} with credibility statements`} />
          <Kpi label="Avg Academic Quality" value={s.sources.avg_quality_academic.toFixed(1)}
               icon={ShieldCheck} sublabel="0–100 scale, target ≥60" />
        </div>
      </section>

      {/* Section: Pending review (the manual-triage queue) */}
      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Pending Review
        </h3>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Kpi label="Pending Retags — Academic" value={s.pending_retags.academic.toLocaleString()}
               icon={Clock} tone={s.pending_retags.academic > 100 ? 'warn' : 'neutral'} />
          <Kpi label="Pending Retags — Practitioner" value={s.pending_retags.practitioner.toLocaleString()}
               icon={Clock} tone={s.pending_retags.practitioner > 500 ? 'warn' : 'neutral'} />
          <Kpi label="New Candidates (Type B)" value={s.candidates.total.toLocaleString()}
               icon={Database}
               sublabel="From discovery pipelines (empty until Phase 5+6)" />
          <Kpi label="Flagged for Review" value={s.sources.flagged_count.toLocaleString()}
               icon={AlertTriangle}
               tone={s.sources.flagged_count > 0 ? 'warn' : 'good'} />
        </div>
      </section>

      {/* Section: Recent curation activity */}
      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Recent Curation Activity (Last 14 Days)
        </h3>
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          {recentLog.isLoading ? (
            <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
          ) : recentLog.data?.items.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">
              No curation actions logged in the last 14 days.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {recentLog.data?.items.map((item) => (
                <li key={item.id} className="flex items-center justify-between px-4 py-3 text-sm">
                  <div className="flex items-center gap-3">
                    {item.action.startsWith('retag_approve') || item.action.includes('publish') ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    ) : item.action.includes('reject') || item.action.includes('delete') ? (
                      <AlertTriangle className="h-4 w-4 text-rose-600" />
                    ) : (
                      <FileText className="h-4 w-4 text-gray-500" />
                    )}
                    <div>
                      <div className="font-medium text-gray-900">
                        {ACTION_LABELS[item.action] || item.action}
                      </div>
                      <div className="text-xs text-gray-500">
                        {item.entity_type}: {item.entity_id.slice(0, 8)}…
                        {item.notes ? ` · ${item.notes}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className="text-right text-xs text-gray-500">
                    <div className="flex items-center gap-1 text-gray-600">
                      <Users className="h-3 w-3" />
                      {item.admin_username || 'system'}
                    </div>
                    <div>{new Date(item.created_at).toLocaleString()}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
